'use client';

import { useState, useEffect } from 'react';
import { FaCalendarAlt, FaTrash, FaSave, FaPlus, FaTimes, FaChevronLeft, FaChevronRight, FaEdit } from 'react-icons/fa';
import { motion } from 'framer-motion';
import calendarService, { PricingRule, WeekdayPricing, CalendarSettings } from '../../../../services/calendarService';
import { toast } from 'react-hot-toast';

// Component props
interface PricingSettingsProps {
  initialDate?: Date;
  onDataChange?: () => void;
}

// Helper type for day of week
type DayOfWeek = 'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday' | 'saturday' | 'sunday';

export default function PricingSettings({ initialDate, onDataChange }: PricingSettingsProps) {
  // State for current date (for preview calendar)
  const [currentDate, setCurrentDate] = useState<Date>(initialDate || new Date());
  // State for loading indicator
  const [isLoading, setIsLoading] = useState<boolean>(true);
  
  // Base price state
  const [basePrice, setBasePrice] = useState(250);
  
  // Weekday pricing adjustments
  const [weekdayPricing, setWeekdayPricing] = useState<WeekdayPricing>({
    monday: 0,
    tuesday: 0,
    wednesday: 0,
    thursday: 0,
    friday: 20,
    saturday: 50,
    sunday: 20
  });
  
  // Seasonal pricing rules
  const [seasonalRules, setSeasonalRules] = useState<PricingRule[]>([]);
  
  // Fetch calendar settings on component mount
  useEffect(() => {
    const fetchCalendarSettings = async () => {
      try {
        setIsLoading(true);
        const settings = await calendarService.getCalendarSettings();
        
        // Update state with fetched settings
        setBasePrice(settings.basePrice);
        setWeekdayPricing(settings.weekdayPricing);
        setSeasonalRules(settings.seasonalRules);
      } catch (error) {
        console.error('Error fetching calendar settings:', error);
        toast.error('Failed to load pricing settings');
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchCalendarSettings();
  }, []);
  
  // State for form visibility
  const [showNewRuleForm, setShowNewRuleForm] = useState<boolean>(false);
  
  // State for new rule
  const [newRule, setNewRule] = useState<Omit<PricingRule, 'id'>>({
    name: '',
    startDate: '',
    endDate: '',
    price: 0,
    description: ''
  });
  
  // State for editing rule
  const [editingRule, setEditingRule] = useState<string | null>(null);
  const [editRule, setEditRule] = useState<PricingRule>({
    id: '',
    name: '',
    startDate: '',
    endDate: '',
    price: 0,
    description: ''
  });
  
  // State for confirmation dialog
  const [showConfirmDialog, setShowConfirmDialog] = useState<boolean>(false);
  const [ruleToDelete, setRuleToDelete] = useState<string | null>(null);
  
  // Handle base price change
  const handleBasePriceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value);
    if (!isNaN(value)) {
      setBasePrice(value);
    }
  };
  
  // Handle weekday price adjustment change
  const handleWeekdayPriceChange = (day: DayOfWeek, e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value);
    if (!isNaN(value)) {
      setWeekdayPricing({
        ...weekdayPricing,
        [day]: value
      });
    }
  };
  
  // Handle input change for new rule form
  const handleNewRuleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setNewRule(prev => ({
      ...prev,
      [name]: name === 'price' ? parseFloat(value) || 0 : value
    }));
  };
  
  // Handle input change for edit rule form
  const handleEditRuleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setEditRule(prev => ({
      ...prev,
      [name]: name === 'price' ? parseFloat(value) || 0 : value
    }));
  };
  
  // Start editing a rule
  const startEditingRule = (rule: PricingRule) => {
    setEditingRule(rule.id);
    setEditRule({ ...rule });
    setShowNewRuleForm(false);
  };
  
  // Cancel editing a rule
  const cancelEditingRule = () => {
    setEditingRule(null);
    setEditRule({
      id: '',
      name: '',
      startDate: '',
      endDate: '',
      price: 0,
      description: ''
    });
  };
  
  // Save edited rule
  const saveEditedRule = async () => {
    if (!editRule.name || !editRule.startDate || !editRule.endDate || !editRule.price || editRule.price <= 0) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    try {
      setIsLoading(true);
      
      // Find the rule in the array and update it
      const updatedRules = seasonalRules.map(rule => 
        rule.id === editingRule ? { ...rule, ...editRule as PricingRule } : rule
      );
      
      // Update calendar settings with the updated rules
      await calendarService.updateCalendarSettings({
        basePrice,
        weekdayPricing,
        seasonalRules: updatedRules,
        blockedPeriods: [] // Preserve blocked periods
      });
      
      // Update local state
      setSeasonalRules(updatedRules);
      setEditingRule(null);
      setEditRule({} as PricingRule);
      
      // Notify parent component of data change
      if (onDataChange) {
        onDataChange();
      }
      
      toast.success('Pricing rule updated successfully');
    } catch (error) {
      console.error('Error updating pricing rule:', error);
      toast.error('Failed to update pricing rule');
    } finally {
      setIsLoading(false);
    }
  };
  
  // Add new pricing rule
  const addNewRule = async () => {
    if (!newRule.name || !newRule.startDate || !newRule.endDate || newRule.price <= 0) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    // Validate date range
    const startDate = new Date(newRule.startDate);
    const endDate = new Date(newRule.endDate);
    
    if (endDate < startDate) {
      toast.error('End date must be after start date');
      return;
    }
    
    try {
      setIsLoading(true);
      const rule: PricingRule = {
        id: `rule-${Date.now()}`,
        name: newRule.name,
        startDate: newRule.startDate,
        endDate: newRule.endDate,
        price: newRule.price,
        description: newRule.description || ''
      };
      
      await calendarService.addPricingRule(rule);
      
      // Update local state
      setSeasonalRules([...seasonalRules, rule]);
      
      // Reset form
      setNewRule({
        name: '',
        startDate: '',
        endDate: '',
        price: 0,
        description: ''
      });
      
      setShowNewRuleForm(false);
      
      // Notify parent component of data change
      if (onDataChange) {
        onDataChange();
      }
      
      toast.success('Pricing rule added successfully');
    } catch (error) {
      console.error('Error adding pricing rule:', error);
      toast.error('Failed to add pricing rule');
    } finally {
      setIsLoading(false);
    }
  };
  
  // Confirm delete pricing rule
  const confirmDeleteRule = (ruleId: string) => {
    setRuleToDelete(ruleId);
    setShowConfirmDialog(true);
  };
  
  // Delete pricing rule
  const deleteRule = async () => {
    if (!ruleToDelete) return;
    
    try {
      setIsLoading(true);
      await calendarService.deletePricingRule(ruleToDelete);
      
      // Update local state
      setSeasonalRules(seasonalRules.filter(rule => rule.id !== ruleToDelete));
      
      // Notify parent component of data change
      if (onDataChange) {
        onDataChange();
      }
      
      toast.success('Pricing rule deleted successfully');
    } catch (error) {
      console.error('Error deleting pricing rule:', error);
      toast.error('Failed to delete pricing rule');
    } finally {
      setIsLoading(false);
      setShowConfirmDialog(false);
      setRuleToDelete(null);
    }
  };
  
  // Save all pricing settings
  const savePricingSettings = async () => {
    try {
      setIsLoading(true);
      
      // Get current settings to preserve blocked periods
      const currentSettings = await calendarService.getCalendarSettings();
      
      // Create settings object
      const settings: CalendarSettings = {
        basePrice,
        weekdayPricing,
        seasonalRules,
        blockedPeriods: currentSettings.blockedPeriods // Preserve blocked periods
      };
      
      // Save settings via service
      await calendarService.updateCalendarSettings(settings);
      
      // Notify parent component of data change
      if (onDataChange) {
        onDataChange();
      }
      
      toast.success('Pricing settings saved successfully');
    } catch (error) {
      console.error('Error saving pricing settings:', error);
      toast.error('Failed to save pricing settings');
    } finally {
      setIsLoading(false);
    }
  };
  
  // Calculate price for a specific date based on all rules
  const calculateDayPrice = (dateString: string): number => {
    const date = new Date(dateString);
    
    // Get day of week (0 = Sunday, 1 = Monday, etc.)
    const dayIndex = date.getDay();
    const dayNames: DayOfWeek[] = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const dayOfWeek = dayNames[dayIndex];
    
    // Start with base price
    let price = basePrice;
    
    // Add weekday adjustment
    price += weekdayPricing[dayOfWeek];
    
    // Check if date falls within any seasonal rule
    for (const rule of seasonalRules) {
      const ruleStart = new Date(rule.startDate);
      const ruleEnd = new Date(rule.endDate);
      
      if (date >= ruleStart && date <= ruleEnd) {
        // Apply seasonal price (override, not add)
        return rule.price;
      }
    }
    
    return price;
  };
  
  // Check if a date falls within any seasonal rule
  const getAppliedSeasonalRule = (dateString: string): PricingRule | null => {
    const date = new Date(dateString);
    
    for (const rule of seasonalRules) {
      const ruleStart = new Date(rule.startDate);
      const ruleEnd = new Date(rule.endDate);
      
      if (date >= ruleStart && date <= ruleEnd) {
        return rule;
      }
    }
    
    return null;
  };
  
  // Generate calendar days for the current month
  const generateCalendarDays = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const firstDayOfMonth = new Date(year, month, 1).getDay();
    
    // Days of week header
    const daysOfWeek = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    
    // Generate calendar days array
    const calendarDays = [];
    
    // Add empty cells for days before the first day of the month
    for (let i = 0; i < firstDayOfMonth; i++) {
      calendarDays.push(null);
    }
    
    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const dateObj = new Date(year, month, day);
      const dateString = dateObj.toISOString().split('T')[0];
      const seasonalRule = getAppliedSeasonalRule(dateString);
      
      calendarDays.push({
        day,
        date: dateString,
        price: calculateDayPrice(dateString),
        seasonalRule: seasonalRule
      });
    }
    
    return {
      daysOfWeek,
      calendarDays
    };
  };
  
  // Change month handler
  const changeMonth = (increment: number) => {
    const newDate = new Date(currentDate);
    newDate.setMonth(newDate.getMonth() + increment);
    setCurrentDate(newDate);
  };
  
  // Generate calendar data
  const { daysOfWeek, calendarDays } = generateCalendarDays();
  
  return (
    <div className="space-y-8 relative">
      {/* Loading overlay */}
      {isLoading && (
        <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-50">
          <div className="text-blue-600 text-lg font-medium">Loading pricing settings...</div>
        </div>
      )}
      {/* Base Price Section */}
      <section>
        <h3 className="text-lg font-medium text-gray-900 mb-4">Base Price</h3>
        <div className="bg-white p-4 rounded-lg border border-gray-200">
          <div className="flex items-center">
            <div className="w-full md:w-1/3">
              <label htmlFor="basePrice" className="block text-sm font-medium text-gray-700 mb-1">
                Standard nightly rate (RM)
              </label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <span className="text-gray-500 sm:text-sm">RM</span>
                </div>
                <input
                  type="number"
                  id="basePrice"
                  className="focus:ring-blue-500 focus:border-blue-500 block w-full pl-12 pr-12 sm:text-sm border-gray-300 rounded-md"
                  value={basePrice}
                  onChange={handleBasePriceChange}
                />
              </div>
            </div>
            <div className="ml-4 text-sm text-gray-500">
              This is the default price per night before any seasonal or day-specific adjustments.
            </div>
          </div>
        </div>
      </section>
      
      {/* Weekday Price Adjustments */}
      <section>
        <h3 className="text-lg font-medium text-gray-900 mb-4">Weekday Price Adjustments</h3>
        <div className="bg-white p-4 rounded-lg border border-gray-200">
          <p className="text-sm text-gray-500 mb-4">
            Set price adjustments for specific days of the week. These are added to or subtracted from the base price.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-7 gap-4">
            {Object.entries(weekdayPricing).map(([day, adjustment]) => (
              <div key={day} className="flex flex-col">
                <label htmlFor={`${day}-adjustment`} className="block text-sm font-medium text-gray-700 capitalize mb-1">
                  {day}
                </label>
                <div className="mt-1 relative rounded-md shadow-sm">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <span className="text-gray-500 sm:text-sm">RM</span>
                  </div>
                  <input
                    type="number"
                    id={`${day}-adjustment`}
                    className="focus:ring-blue-500 focus:border-blue-500 block w-full pl-12 pr-12 sm:text-sm border-gray-300 rounded-md"
                    value={adjustment}
                    onChange={(e) => handleWeekdayPriceChange(day as DayOfWeek, e)}
                  />
                </div>
              </div>
            ))}
          </div>
          
          <div className="mt-4 p-3 bg-blue-50 rounded-md">
            <p className="text-sm text-blue-700">
              Example: If base price is RM{basePrice} and Saturday adjustment is RM{weekdayPricing.saturday}, 
              then Saturday's price will be RM{basePrice + weekdayPricing.saturday}.
            </p>
          </div>
        </div>
      </section>
      
      {/* Pricing Preview Calendar */}
      <section>
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium text-gray-900">Pricing Preview</h3>
          <div className="flex items-center space-x-4">
            <button 
              onClick={() => changeMonth(-1)}
              className="px-3 py-1 border border-gray-300 rounded-md text-sm flex items-center"
            >
              <FaChevronLeft className="mr-1" /> Previous
            </button>
            <span className="font-medium">
              {currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
            </span>
            <button 
              onClick={() => changeMonth(1)}
              className="px-3 py-1 border border-gray-300 rounded-md text-sm flex items-center"
            >
              Next <FaChevronRight className="ml-1" />
            </button>
          </div>
        </div>
        
        <div className="bg-white p-4 rounded-lg border border-gray-200">
          {/* Calendar grid */}
          <div className="grid grid-cols-7 gap-1">
            {/* Days of week header */}
            {daysOfWeek.map(day => (
              <div key={day} className="text-center font-medium text-sm text-gray-500 py-2">
                {day}
              </div>
            ))}
            
            {/* Calendar days */}
            {calendarDays.map((day, index) => (
              <motion.div 
                key={index} 
                className={`aspect-square border rounded-md flex flex-col items-center justify-center p-1
                  ${!day ? 'bg-gray-50' : 
                    day.seasonalRule ? 'bg-purple-50 hover:bg-purple-100' : 'bg-white hover:bg-gray-50'}`}
                whileHover={{ scale: 1.05 }}
                transition={{ duration: 0.2 }}
              >
                {day ? (
                  <>
                    <div className="text-sm font-medium">{day.day}</div>
                    <div className={`text-xs font-bold mt-1 ${day.seasonalRule ? 'text-purple-700' : 'text-blue-700'}`}>
                      RM {day.price}
                    </div>
                    {day.seasonalRule && (
                      <div className="text-xs text-purple-600 mt-1 truncate max-w-full px-1" title={day.seasonalRule.name}>
                        {day.seasonalRule.name.length > 10 
                          ? `${day.seasonalRule.name.substring(0, 8)}...` 
                          : day.seasonalRule.name}
                      </div>
                    )}
                  </>
                ) : null}
              </motion.div>
            ))}
          </div>
          
          <div className="mt-4 flex justify-center items-center space-x-6 text-xs text-gray-500">
            <div className="flex items-center">
              <div className="w-3 h-3 rounded-full bg-blue-700 mr-1"></div>
              <span>Base Price + Weekday Adjustment</span>
            </div>
            <div className="flex items-center">
              <div className="w-3 h-3 rounded-full bg-purple-700 mr-1"></div>
              <span>Seasonal Rule Applied</span>
            </div>
          </div>
        </div>
      </section>

    {/* Seasonal Pricing Rules */}
    <section>
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-medium text-gray-900">Seasonal Pricing Rules</h3>
        <button
          onClick={() => setShowNewRuleForm(!showNewRuleForm)}
          className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm flex items-center"
          disabled={isLoading}
        >
          <FaPlus className="mr-1" /> Add Rule
        </button>
      </div>

      {/* New Rule Form */}
      {showNewRuleForm && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
          transition={{ duration: 0.3 }}
          className="bg-gray-50 p-4 rounded-lg mb-4"
        >
          <h4 className="font-medium text-gray-900 mb-3">Add New Seasonal Pricing Rule</h4>

          <div className="space-y-3">
            <div>
              <label htmlFor="ruleName" className="block text-sm font-medium text-gray-700">Rule Name</label>
              <input
                type="text"
                id="ruleName"
                name="name"
                value={newRule.name}
                onChange={handleNewRuleChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                placeholder="e.g. Summer Season"
              />
            </div>

            <div>
              <label htmlFor="startDate" className="block text-sm font-medium text-gray-700 mb-1">
                Start Date
              </label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <FaCalendarAlt className="text-gray-400" />
                </div>
                <input
                  type="date"
                  id="startDate"
                  name="startDate"
                  value={newRule.startDate}
                  onChange={handleNewRuleChange}
                  className="focus:ring-blue-500 focus:border-blue-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md"
                />
              </div>
            </div>

            <div>
              <label htmlFor="endDate" className="block text-sm font-medium text-gray-700 mb-1">
                End Date
              </label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <FaCalendarAlt className="text-gray-400" />
                </div>
                <input
                  type="date"
                  id="endDate"
                  name="endDate"
                  value={newRule.endDate}
                  onChange={handleNewRuleChange}
                  className="focus:ring-blue-500 focus:border-blue-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md"
                />
              </div>
            </div>

            <div>
              <label htmlFor="price" className="block text-sm font-medium text-gray-700 mb-1">
                Price (RM)
              </label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <span className="text-gray-500 sm:text-sm">RM</span>
                </div>
                <input
                  type="number"
                  id="price"
                  name="price"
                  value={newRule.price || ''}
                  onChange={handleNewRuleChange}
                  className="focus:ring-blue-500 focus:border-blue-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md"
                  placeholder="e.g., 300"
                />
              </div>
            </div>
              
            <div className="md:col-span-2">
              <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
                Description (Optional)
              </label>
              <textarea
                id="description"
                name="description"
                rows={2}
                className="focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md"
                value={newRule.description || ''}
                onChange={handleNewRuleChange}
                placeholder="Add details about this seasonal pricing"
              />
            </div>
          </div>
            
            <div className="flex justify-end space-x-3 mt-4">
              <button
                onClick={() => setShowNewRuleForm(false)}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={addNewRule}
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
                disabled={!newRule.name || !newRule.startDate || !newRule.endDate || !newRule.price}
              >
                Add Season
              </button>
            </div>
          </motion.div>
        )}
        
        {/* List of existing seasonal pricing rules */}
        <div className="mt-6 bg-white rounded-lg border border-gray-200 overflow-hidden">
          {seasonalRules.length === 0 ? (
            <div className="p-6 text-center text-gray-500">
              No seasonal pricing rules defined yet. Add your first rule above.
            </div>
          ) : (
            <ul className="divide-y divide-gray-200">
              {seasonalRules.map((rule) => (
                <li key={rule.id} className="p-4 hover:bg-gray-50">
                  {editingRule === rule.id ? (
                    <motion.div 
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="space-y-3"
                    >
                      <div>
                        <label htmlFor={`edit-name-${rule.id}`} className="block text-sm font-medium text-gray-700">Rule Name</label>
                        <input
                          type="text"
                          id={`edit-name-${rule.id}`}
                          name="name"
                          value={editRule.name}
                          onChange={handleEditRuleChange}
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                        />
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div>
                          <label htmlFor={`edit-startDate-${rule.id}`} className="block text-sm font-medium text-gray-700">Start Date</label>
                          <div className="mt-1 relative rounded-md shadow-sm">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                              <FaCalendarAlt className="text-gray-400" />
                            </div>
                            <input
                              type="date"
                              id={`edit-startDate-${rule.id}`}
                              name="startDate"
                              value={editRule.startDate}
                              onChange={handleEditRuleChange}
                              className="focus:ring-blue-500 focus:border-blue-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md"
                            />
                          </div>
                        </div>
                        
                        <div>
                          <label htmlFor={`edit-endDate-${rule.id}`} className="block text-sm font-medium text-gray-700">End Date</label>
                          <div className="mt-1 relative rounded-md shadow-sm">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                              <FaCalendarAlt className="text-gray-400" />
                            </div>
                            <input
                              type="date"
                              id={`edit-endDate-${rule.id}`}
                              name="endDate"
                              value={editRule.endDate}
                              onChange={handleEditRuleChange}
                              className="focus:ring-blue-500 focus:border-blue-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md"
                            />
                          </div>
                        </div>
                      </div>
                      
                      <div>
                        <label htmlFor={`edit-price-${rule.id}`} className="block text-sm font-medium text-gray-700">Price (RM)</label>
                        <div className="mt-1 relative rounded-md shadow-sm">
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <span className="text-gray-500 sm:text-sm">RM</span>
                          </div>
                          <input
                            type="number"
                            id={`edit-price-${rule.id}`}
                            name="price"
                            value={editRule.price || ''}
                            onChange={handleEditRuleChange}
                            className="focus:ring-blue-500 focus:border-blue-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md"
                          />
                        </div>
                      </div>
                      
                      <div>
                        <label htmlFor={`edit-description-${rule.id}`} className="block text-sm font-medium text-gray-700">Description (Optional)</label>
                        <textarea
                          id={`edit-description-${rule.id}`}
                          name="description"
                          rows={2}
                          value={editRule.description || ''}
                          onChange={handleEditRuleChange}
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                        />
                      </div>
                      
                      <div className="flex justify-end space-x-3 mt-3">
                        <button
                          onClick={cancelEditingRule}
                          className="px-3 py-1 border border-gray-300 rounded-md text-sm text-gray-700 bg-white hover:bg-gray-50"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={saveEditedRule}
                          className="px-3 py-1 border border-transparent rounded-md shadow-sm text-sm text-white bg-blue-600 hover:bg-blue-700"
                        >
                          <FaSave className="inline mr-1" /> Save
                        </button>
                      </div>
                    </motion.div>
                  ) : (
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="font-medium text-gray-900">{rule.name}</h4>
                        <div className="mt-1 text-sm text-gray-500">
                          <span className="mr-3">{new Date(rule.startDate).toLocaleDateString()} - {new Date(rule.endDate).toLocaleDateString()}</span>
                          <span className="font-medium text-blue-600">RM {rule.price}</span>
                        </div>
                        {rule.description && (
                          <p className="mt-1 text-sm text-gray-500">{rule.description}</p>
                        )}
                      </div>
                      <div className="flex space-x-2">
                        <button
                          onClick={() => startEditingRule(rule)}
                          className="p-1 text-gray-500 hover:text-blue-600"
                          title="Edit rule"
                        >
                          <FaEdit />
                        </button>
                        <button
                          onClick={() => confirmDeleteRule(rule.id)}
                          className="p-1 text-gray-500 hover:text-red-600"
                          title="Delete rule"
                        >
                          <FaTrash />
                        </button>
                      </div>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>
      
      {/* Confirmation Dialog for Delete */}
      {showConfirmDialog && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full shadow-xl">
            <h3 className="text-lg font-medium text-gray-900 mb-3">Confirm Delete</h3>
            <p className="text-gray-500 mb-5">Are you sure you want to delete this pricing rule? This action cannot be undone.</p>
            
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowConfirmDialog(false)}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={deleteRule}
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Save Button */}
      <div className="flex justify-end mt-8">
        <button
          onClick={savePricingSettings}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700"
        >
          <FaSave className="mr-2" /> Save Pricing Settings
        </button>
      </div>
    </div>
  );
}
